import { useEffect, useRef, useState } from 'react';
import './AudioLevelMeter.css';

interface AudioLevelMeterProps {
  stream: MediaStream | null;
}

export default function AudioLevelMeter({ stream }: AudioLevelMeterProps) {
  const [level, setLevel] = useState(0);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const analyserRef = useRef<AnalyserNode | undefined>(undefined);
  const audioContextRef = useRef<AudioContext | undefined>(undefined);

  useEffect(() => {
    if (!stream) {
      setLevel(0);
      return;
    }

    // Create audio context and analyser
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const audioContext = new AudioContextClass();
    const analyser = audioContext.createAnalyser();
    const microphone = audioContext.createMediaStreamSource(stream);

    analyser.smoothingTimeConstant = 0.3;
    analyser.fftSize = 2048;

    microphone.connect(analyser);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.fftSize);

    const updateLevel = () => {
      if (!analyserRef.current) return;

      // Use time-domain data for better dynamic range
      analyserRef.current.getByteTimeDomainData(dataArray);

      // Calculate RMS (Root Mean Square) for accurate volume
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const normalized = (dataArray[i] - 128) / 128;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / dataArray.length);

      // Convert to percentage with better scaling
      // Multiply by a factor to get useful range (typical speech is around 0.1-0.3 RMS)
      const normalizedLevel = Math.min(100, rms * 300);

      setLevel(normalizedLevel);
      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };

    updateLevel();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stream]);

  const getColorClass = () => {
    if (level < 30) return 'low';
    if (level < 70) return 'medium';
    return 'high';
  };

  return (
    <div className="audio-level-meter">
      <div className="meter-label">Audio Level</div>
      <div className="meter-container">
        <div
          className={`meter-bar ${getColorClass()}`}
          style={{ width: `${level}%` }}
        />
      </div>
      <div className="meter-value">{Math.round(level)}%</div>
    </div>
  );
}
